<script>
import Form from './Form.vue'
import FormPart from './FormPart.vue'
import Field from '../field/Field.vue'
import Input from '../input/Input.vue'
import Checkbox from '../checkbox/Checkbox.vue'
import CheckboxGroup from '../checkboxGroup/CheckboxGroup.vue'
import RadioGroup from '../radioGroup/RadioGroup.vue'
import Select from '../select/Select.vue'
import { merge } from '../../utils/helpers'
import { h, ref, defineComponent, onBeforeMount, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-dynamic-form></ak-dynamic-form>
   */
  name: 'AkDynamicForm',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Form.name]: Form,
    [FormPart.name]: FormPart,
    [Field.name]: Field,
    [Input.name]: Input,
    [Checkbox.name]: Checkbox,
    [CheckboxGroup.name]: CheckboxGroup,
    [RadioGroup.name]: RadioGroup,
    [Select.name]: Select,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Mixed}
     * @description the model value for the component
     */
    modelValue: {
      type: Object,
      default: () => ({}),
    },

    /**
     * @type {Object}
     * @description The form object that we want to render.
     */
    form: {
      type: Object,
      default: () => ({
        parts: [],
      }),
    },

    /**
     * @type {Boolean}
     * @description Determines if the parts will show the back button
     */
    showBack: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the parts will show the part counter
     */
    showCount: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const form = ref(props.form)

    /**
     * @type {Object}
     * @description A reactive object containing the form data model for the
     * dynamic form component
     */
    const formData = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    /**
     * @param {Event} event
     * @description Handles the submission of the form, this will try to run
     * the validation on all of the fields before submitting anything.
     */
    const handleSubmission = (event) => {
      console.log('Form submitted: ', event)
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the base options all fields will have
     */
    const baseFieldProps = (field) => {
      return {
        required: field.rules && field.rules.required,
        type: field.type,
        name: field.name,
        modelValue: formData.value[field.name],
        maxlength: field.rules && field.rules.maxlength,
        minlength: field.rules && field.rules.minlength,
        'onUpdate:modelValue': (value) => (formData.value[field.name] = value),
      }
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render a select field
     * component in the form part
     */
    const renderSelect = (field) => {
      const baseOptions = baseFieldProps(field)
      return h(
        Select,
        merge(
          baseOptions,
          {
            options: field.options,
          },
          true
        )
      )
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render a radio group field
     * component in the form part
     */
    const renderRadio = (field) => {
      const baseOptions = baseFieldProps(field)
      return h(
        RadioGroup,
        merge(
          baseOptions,
          {
            vertical: field.vertical,
            options: field.options,
          },
          true
        )
      )
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render a checbox group field
     * component in the form part
     */
    const renderCheckboxGroup = (field) => {
      const baseOptions = baseFieldProps(field)
      return h(
        CheckboxGroup,
        merge(
          baseOptions,
          {
            vertical: field.vertical,
            options: field.options,
          },
          true
        )
      )
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render a checkbox field
     * component in the form part
     */
    const renderCheckbox = (field) => {
      const baseOptions = baseFieldProps(field)
      return h(
        Checkbox,
        merge(
          baseOptions,
          {
            label: true,
          },
          true
        ),
        () => [field.label]
      )
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render an input field
     * component in the form part
     */
    const renderInput = (field) => {
      const baseOptions = baseFieldProps(field)
      return h(Input, merge(baseOptions, {}))
    }

    /**
     * @param {Object} field
     * @returns {Object}
     * @description Returns the AST object to render a field in a
     * form part.
     */
    const renderField = (field) => {
      switch (field.tag) {
        case 'ak-select':
          return renderSelect(field)
        case 'ak-radio':
          return renderRadio(field)
        case 'ak-checkbox-group':
          return renderCheckboxGroup(field)
        case 'ak-checkbox':
          return renderCheckbox(field)
        default:
          return renderInput(field)
      }
    }

    /**
     * @param {Object} part
     * @returns {Array}
     * @description Returns the AST object to render the fields in the
     * form part
     */
    const renderFields = (part) => {
      return part.fields.map((field) => {
        const label = field.tag !== 'ak-checkbox' ? field.label : ''
        const required = (field.rules && field.rules.required) || false
        return h(Field, { label, required }, () => [renderField(field)])
      })
    }

    /**
     * @returns {Array}
     * @description Returns the AST object to render the form parts.
     */
    const renderParts = () => {
      return form.value.parts.map((part, index) => {
        return h(
          FormPart,
          {
            ...part,
            pageNumber: props.showCount,
            showBack: index !== 0 && props.showBack,
          },
          () => renderFields(part)
        )
      })
    }

    /**
     * @description Returns the AST object to render the form.
     */
    const renderForm = () => {
      return h(
        Form,
        {
          onSubmit: handleSubmission,
        },
        () => renderParts(h)
      )
    }

    onBeforeMount(() => {
      if (
        form.value &&
        Array.isArray(form.value.parts) &&
        form.value.parts.length
      ) {
        const fields = [].concat.apply(...form.value.parts.map((i) => i.fields))
        for (const [, value] of Object.entries(fields)) {
          formData.value[value.name] = undefined
        }
      }
    })

    return () => h('div', { class: 'ak-form' }, [renderForm()])
  },
})
</script>
